import { FunctionComponent, useMemo } from "react";

import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";

import { SalesforcePipeline } from "../schema/salesforceDeal";
import {
	isSynthesisedPipeline,
	resolveSelectableStageId,
	stagesOfPipeline,
} from "./salesforcePipeline.utils";

type Ts = (key: string) => string;

interface SalesforcePipelinePickerProps {
	pipelines: SalesforcePipeline[];
	pipelineId: string | null;
	stageId: string | null;
	onChange: (next: { pipelineId: string | null; stageId: string | null }) => void;
	ts: Ts;
}

/**
 * The opportunity record type + stage pair.
 *
 * Salesforce has no pipeline object: a record type selects the Sales Process and therefore
 * which `StageName` values an Opportunity may use. Orgs frequently run none, in which case
 * `GET pipelines` synthesises exactly one pipeline over the whole active `StageName` picklist
 * — labelled as such, because "no record types" is a fact about the customer's org rather
 * than an error.
 *
 * Switching the record type always clears the stage: the stored one belongs to the previous
 * sales process and the org would reject it on write.
 */
export const SalesforcePipelinePicker: FunctionComponent<SalesforcePipelinePickerProps> = ({
	pipelines,
	pipelineId,
	stageId,
	onChange,
	ts,
}) => {
	const pipelineOptions: DropdownAutoCompleteItem[] = useMemo(
		() => pipelines.map((pipeline) => ({ id: pipeline.id, title: pipeline.label })),
		[pipelines]
	);

	const stageOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			stagesOfPipeline(pipelines, pipelineId).map((stage) => ({
				id: stage.id,
				title: stage.label,
			})),
		[pipelines, pipelineId]
	);

	// A stage from another record type (or from a pipeline that has since disappeared) shows
	// as unset rather than as a selection the org would reject.
	const selectableStageId = resolveSelectableStageId(pipelines, pipelineId, stageId);
	const isDefaultPipeline = isSynthesisedPipeline(pipelines, pipelineId);

	return (
		<>
			<div className="flex flex-col gap-1">
				<Dropdown
					data={pipelineOptions}
					defaultValue={pipelineOptions.find((option) => option.id === pipelineId)}
					floating={true}
					handleSelect={(value) => onChange({ pipelineId: value.id as string, stageId: null })}
					information={ts("pipelineHelp")}
					label={ts("defaultPipeline")}
				/>
				{isDefaultPipeline && (
					<span className="text-xs text-text-meta" data-testid="salesforce-default-pipeline-note">
						{ts("defaultPipelineNote")}
					</span>
				)}
			</div>

			<Dropdown
				key={pipelineId ?? "no-pipeline"}
				data={stageOptions}
				defaultValue={stageOptions.find((option) => option.id === selectableStageId)}
				disabled={!pipelineId}
				floating={true}
				handleSelect={(value) => onChange({ pipelineId, stageId: value.id as string })}
				label={ts("defaultStage")}
			/>
		</>
	);
};
